import {
  ChangeDetectionStrategy,
  Component,
  LOCALE_ID,
  computed,
  inject,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import {
  DepartmentListFilters,
  DepartmentSortBy,
  DepartmentSortChange,
} from '../../../../../pages/admin-dashboard/departments/departments.model';
import { FilterDateComponent } from '../../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterSortingComponent } from '../../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { FilterInput } from '../../../../../shared/ui/shared-filters/filter-input/filter-input';
import { SubmitButton } from '../../../../../shared/ui/Buttons/submit-button/submit-button';

@Component({
  selector: 'app-departments-list-filter',
  imports: [FilterInput, FilterDateComponent, FilterSortingComponent, SubmitButton],
  templateUrl: './departments-list-filter.html',
  styleUrl: './departments-list-filter.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepartmentsListFilter {
  private locale = inject(LOCALE_ID);

  filters = input.required<DepartmentListFilters>();

  applyFilters = output<DepartmentListFilters>();
  resetFilters = output();
  sortChange = output<DepartmentSortChange>();

  private nameSource = computed(() => this.filters().departmentName);
  private createdFromSource = computed(() => this.filters().createdOnFrom);
  private createdToSource = computed(() => this.filters().createdOnTo);
  private modifiedFromSource = computed(() => this.filters().lastModifiedOnFrom);
  private modifiedToSource = computed(() => this.filters().lastModifiedOnTo);

  departmentName = linkedSignal<string>(() => this.nameSource());
  createdOnFrom = linkedSignal<string | null>(() => this.createdFromSource() || null);
  createdOnTo = linkedSignal<string | null>(() => this.createdToSource() || null);
  lastModifiedOnFrom = linkedSignal<string | null>(() => this.modifiedFromSource() || null);
  lastModifiedOnTo = linkedSignal<string | null>(() => this.modifiedToSource() || null);

  createdDateInvalid = linkedSignal<boolean>(() => {
    this.createdFromSource();
    this.createdToSource();
    return false;
  });
  lastModifiedDateInvalid = linkedSignal<boolean>(() => {
    this.modifiedFromSource();
    this.modifiedToSource();
    return false;
  });

  isArabic = computed(() => this.locale.startsWith('ar'));

  sortOptions = computed(() => [
    { value: 'TITLE', labelEn: 'Title', labelAr: 'العنوان' },
    { value: 'CREATION_DATE', labelEn: 'Created On', labelAr: 'تاريخ الإنشاء' },
    { value: 'LAST_MODIFICATION_DATE', labelEn: 'Last Modified On', labelAr: 'آخر تعديل' },
  ]);

  onOrderByChange(value: string) {
    this.sortChange.emit({ orderBy: value as DepartmentSortBy, orderDir: this.filters().orderDir });
  }

  onToggleSortOrder() {
    this.sortChange.emit({
      orderBy: this.filters().orderBy,
      orderDir: this.filters().orderDir === 'DESC' ? 'ASC' : 'DESC',
    });
  }

  onResetFilters() {
    this.departmentName.set('');
    this.createdOnFrom.set(null);
    this.createdOnTo.set(null);
    this.lastModifiedOnFrom.set(null);
    this.lastModifiedOnTo.set(null);
    this.createdDateInvalid.set(false);
    this.lastModifiedDateInvalid.set(false);
    this.resetFilters.emit();
  }

  onApplyFilters() {
    const createdInvalid = !!(
      this.createdOnFrom() &&
      this.createdOnTo() &&
      this.createdOnFrom()! > this.createdOnTo()!
    );
    const lastModInvalid = !!(
      this.lastModifiedOnFrom() &&
      this.lastModifiedOnTo() &&
      this.lastModifiedOnFrom()! > this.lastModifiedOnTo()!
    );

    this.createdDateInvalid.set(createdInvalid);
    this.lastModifiedDateInvalid.set(lastModInvalid);

    if (createdInvalid || lastModInvalid) return;

    this.applyFilters.emit({
      departmentName: this.departmentName().trim(),
      createdOnFrom: this.createdOnFrom() ?? '',
      createdOnTo: this.createdOnTo() ?? '',
      lastModifiedOnFrom: this.lastModifiedOnFrom() ?? '',
      lastModifiedOnTo: this.lastModifiedOnTo() ?? '',
      orderBy: this.filters().orderBy,
      orderDir: this.filters().orderDir,
    });
  }
}
